<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import { getCaps, Cap, deleteCap } from '@/app/core/services/caps'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')

const loading = ref(false)

/* Datos */
const search = ref()
const tableCaps = ref(null)
const capForDelete = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('caps.list.code'), table: 'code' },
  { name: 'name', title: t('caps.list.name'), table: 'name' },
  {
    name: 'applies_from',
    title: t('caps.list.appliesFrom'),
    table: 'applies_from'
  },
  { name: 'updated_at', title: t('caps.list.lastUpdate'), table: 'updated_at' },
  { name: 'actions', title: '' }
])
const rows = ref<Cap[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createCap = () => {
  void router.push({ name: 'CreateCap' })
}
const updateCap = (id: any) => {
  void router.push({ name: 'UpdateCap', params: { id } })
}
const getCap = (id: any) => {
  void router.push({ name: 'GetCap', params: { id } })
}

/* Eliminar Tope */
const removeCap = (values: any) => {
  showModal.value = true
  titleModal.value = t('caps.list.actionStatus', { cap: values.name as string })
  capForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteCap(capForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('caps.notification.action', [t('notification.wasRemoved')]),
        type: 'success',
        time: 10000
      })
      void caps()
    })
    .catch(async (error: any) => {
      notificationError(error, 'caps', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar Topes */
const caps = async () => {
  loading.value = true
  try {
    const data: any = await getCaps(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'caps', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="t('modal.description')"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('table.action.delete')
      })
    "
    @closeModal="closeModal"
    @otherMethod="deleteConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('caps.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('caps.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="caps" />
          <DBtn @click.prevent="caps" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createCap()"
          v-if="store.hasPermission('cap:create')">
          {{ t('form.button.create', { form: t('caps.list.title', 0) }) }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableCaps"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), caps()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), caps()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('cap:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="getCap(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('cap:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="updateCap(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('cap:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="removeCap(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
